import { AsyncPipe, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseDrawerService } from '@fuse/components/drawer/drawer.service';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { HeaderTextComponent } from '@shared/components/header-text/header-text.component';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { DocumuentoMdComponent } from 'app/layout/common/documento-md/documento-md.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { AppTourService } from '@shared/service/app/tour.service';
import { HeaderTextService } from '@shared/components/header-text/header-text.service';
import { TicketService } from 'app/modules/soporte/services/ticket.service';

@Component({
    selector     : 'dense-layout',
    templateUrl  : './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports      : [FuseLoadingBarComponent, FuseVerticalNavigationComponent, MatButtonModule, MatIconModule, MatTooltipModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, NotificationsComponent, UserComponent, NgIf, AsyncPipe, RouterOutlet, QuickChatComponent, HeaderTextComponent,
        DocumuentoMdComponent
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall: boolean;
    navigation: Navigation;
    navigationAppearance: 'default' | 'dense' = 'dense';
    color_primario: string;
    color_secundario: string;
    default_color: string = 'dark bg-gray-900 print:hidden ';
    logo: string = '../../assets/images/logo/JEDSTION_rayo_lima.svg';
    rutaActual: string;
    isHelpAvailable$: Observable<boolean>;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        protected _router: Router,
        private _navigationService: NavigationService,
        private _empresaService: EmpresaService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _tourService: AppTourService,
        public headerTextService: HeaderTextService,
        private _fuseDrawerService: FuseDrawerService,
        private _ticketService: TicketService
    )
    {
    }

    /**
     * Abre el drawer de carga de ticket en modo nuevo
     */
    abrirTicketDrawer(): void {
        this._ticketService.setActiveTicket(null);
        const drawer = this._fuseDrawerService.getComponent('ticketDrawer');
        if (drawer) {
            drawer.open();
        }
    }

    /**
     * Dispara la ayuda contextual del módulo activo
     */
    startContextualTour(): void {
        this._tourService.requestHelp();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) =>
            {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall ? 'default' : 'dense';
            });
        
        // Subscribe to empresa data
        this._empresaService.empresa$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(empresa => {
                this.color_primario = empresa.color_primario;
                this.color_secundario = empresa.color_secundario;
                this.logo = empresa?.logo || this.logo;
            });

        // Load empresa data
        this._empresaService.getEmpresa();

        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                this.rutaActual = event.urlAfterRedirects;
            });

        this.isHelpAvailable$ = this._tourService.isHelpAvailable$;

        // inicial (por si recargan la página)
        this.rutaActual = this._router.url;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void
    {
        this.navigationAppearance = (this.navigationAppearance === 'default' ? 'dense' : 'default');
    }
}
